/*
 * Notification that the teacher has created (assign/save) assignment(s).
 */
import {plural, t} from "@lingui/core/macro";
import * as React from "react";

import {type UserHasChildAssignmentClassrooms} from "@khan/assignments";
import {BaseToastNotification} from "@khan/component-toast";

type ToastProps = {
    testId?: string;
    linkText?: string;
    href?: string;
};

const ToastCopyMap = {
    ASSIGN: {
        ERROR: (
            requestedContentCount: number,
            failedCount: number,
            title: string,
        ) => {
            if (requestedContentCount === 1 && !!title.length) {
                return t`Oops! Looks like assigning “${title}” failed. Please try again!`;
            }
            return t`Oops! Looks like assigning ${failedCount} assignments failed. Please try again!`;
        },
        SINGLE_CONTENT_SINGLE_CLASS: (
            title: string,
            studentCount: number,
            studentListName: string,
        ) => {
            if (studentCount === 0) {
                return t({
                    context:
                        "Used when assigning a single item to a single empty class.",
                    message: `You assigned “${title}” to future students in your class ${studentListName}.`,
                });
            }
            return t({
                context: "Used when assigning a single item to a single class.",

                message: plural(studentCount, {
                    one: `You assigned “${title}” to # student in your class ${studentListName}.`,
                    other: `You assigned “${title}” to # students in your class ${studentListName}.`,
                }),
            });
        },
        SINGLE_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM: (
            title: string,
            studentListName: string,
        ) => {
            return t({
                context:
                    "Used when assigning a single item to a single child assignments classroom.",
                message: `You assigned “${title}” to ${studentListName}.`,
            });
        },
        SINGLE_CONTENT_MULTI_CLASS: (title: string, classCount: number) => {
            return t({
                context:
                    "Used when assigning a single item to multiple classes.",
                message: `You assigned “${title}” to ${classCount} classes.`,
            });
        },
        SINGLE_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOMS: (
            title: string,
            classCount: number,
        ) => {
            return t({
                context:
                    "Used when assigning a single item to multiple child assignments classrooms.",
                message: `You assigned “${title}” to ${classCount} children.`,
            });
        },
        MULTI_CONTENT_SINGLE_CLASS: (
            contentCount: number,
            studentCount: number,
            studentListName: string,
        ) => {
            if (studentCount === 0) {
                return t({
                    context:
                        "Used when assigning multiple items to a single empty class.",
                    message: `You created ${contentCount} assignments for future students in your class ${studentListName}.`,
                });
            }
            return t({
                context:
                    "Used when assigning multiple items to a single class.",

                message: plural(studentCount, {
                    one: `You assigned ${contentCount} items to # student in your class ${studentListName}.`,
                    other: `You assigned ${contentCount} items to # students in your class ${studentListName}.`,
                }),
            });
        },
        MULTI_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM: (
            contentCount: number,
            studentListName: string,
        ) => {
            return t({
                context:
                    "Used when assigning multiple items to a single class.",
                message: `You assigned ${contentCount} items to ${studentListName}.`,
            });
        },
        MULTI_CONTENT_MULTI_CLASS: (
            contentCount: number,
            classCount: number,
        ) => {
            return t({
                context:
                    "Used when assigning multiple items to multiple classes.",
                message: `You assigned ${contentCount} items to ${classCount} classes.`,
            });
        },
        MULTI_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOM: (
            contentCount: number,
            classCount: number,
        ) => {
            return t({
                context:
                    "Used when assigning multiple items to multiple child assignments classrooms.",
                message: `You assigned ${contentCount} items to ${classCount} children.`,
            });
        },
    },
    SAVE: {
        ERROR: (
            requestedContentCount: number,
            failedCount: number,
            title?: string | null,
        ) => {
            if (requestedContentCount === 1 && !!title?.length) {
                return t`Oops! Looks like saving “${title}” failed. Please try again!`;
            }
            return t`Oops! Looks like saving ${failedCount} assignments failed. Please try again!`;
        },
        SINGLE_CONTENT_SINGLE_CLASS: (
            title: string,
            studentCount: number,
            studentListName: string,
        ) => {
            if (studentCount === 0) {
                return t({
                    context:
                        "Used when saving a single item to a single empty class.",
                    message: `You saved “${title}” for future students in your class ${studentListName}.`,
                });
            }
            return t({
                context: "Used when saving a single item to a single class.",

                message: plural(studentCount, {
                    one: `You saved “${title}” to # student in your class ${studentListName}.`,
                    other: `You saved “${title}” to # students in your class ${studentListName}.`,
                }),
            });
        },
        SINGLE_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM: (
            title: string,
            studentListName: string,
        ) => {
            return t({
                context:
                    "Used when saving a single item to a single child assignments classroom.",
                message: `You saved “${title}” to ${studentListName}.`,
            });
        },
        SINGLE_CONTENT_MULTI_CLASS: (title: string, classCount: number) =>
            t({
                context: "Used when saving a single item to multiple classes.",
                message: `You saved “${title}” to ${classCount} classes.`,
            }),
        SINGLE_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOMS: (
            title: string,
            classCount: number,
        ) => {
            return t({
                context:
                    "Used when saving a single item to multiple child assignments classrooms.",
                message: `You saved “${title}” to ${classCount} children.`,
            });
        },
        MULTI_CONTENT_SINGLE_CLASS: (
            contentCount: number,
            studentCount: number,
            studentListName: string,
        ) => {
            if (studentCount === 0) {
                return t({
                    context:
                        "Used when saving multiple items to a single empty class.",
                    message: `You saved ${contentCount} items for future students in your class ${studentListName}.`,
                });
            }
            return t({
                context: "Used when saving multiple items to a single class.",

                message: plural(studentCount, {
                    one: `You saved ${contentCount} items to # student in your class ${studentListName}.`,
                    other: `You saved ${contentCount} items to # students in your class ${studentListName}.`,
                }),
            });
        },
        MULTI_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM: (
            contentCount: number,
            studentListName: string,
        ) => {
            return t({
                context: "Used when saving multiple items to a single class.",
                message: `You saving ${contentCount} items to ${studentListName}.`,
            });
        },
        MULTI_CONTENT_MULTI_CLASS: (contentCount: number, classCount: number) =>
            t({
                context: "Used when saving multiple items to multiple classes.",
                message: `You saved ${contentCount} items to ${classCount} classes.`,
            }),
        MULTI_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOM: (
            contentCount: number,
            classCount: number,
        ) => {
            return t({
                context:
                    "Used when saved multiple items to multiple child assignments classrooms.",
                message: `You saved ${contentCount} items to ${classCount} children.`,
            });
        },
    },
} as const;

const isInAssignmentsTab = () => {
    const assignmentsRe = /\/coach\/class\/\d+\/(manage-)?assignments/;
    // TODO(jangmi, CLASS-4844): Use withLocation to access SSR safe location.
    // eslint-disable-next-line no-restricted-syntax
    return window.location.pathname.match(assignmentsRe);
};

export default (
    props:
        | {isDuplicateError: true}
        | {
              isDuplicateError?: false;
              createAction: "ASSIGN" | "SAVE";
              successContentCount: number;
              successClassCount: number;
              requestedClassCount: number;
              requestedContentCount: number;
              studentList?: {
                  name: string;
                  id: string;
                  signupCode: string;
              };
              contentTitle: string;
              studentCount: number;
              assignedToChildAssignmentsClassroom?: UserHasChildAssignmentClassrooms;
          },
): React.ReactElement => {
    if (props.isDuplicateError) {
        return (
            <BaseToastNotification isError={true}>
                {t`Oops! You have already created this assignment for these student(s) with this due date.`}
            </BaseToastNotification>
        );
    }

    const {
        createAction = "ASSIGN",
        successContentCount = 0,
        successClassCount = 0,
        requestedClassCount = 0,
        requestedContentCount = 0,
        studentList,
        contentTitle = "",
        studentCount = 0,
        assignedToChildAssignmentsClassroom = "NONE",
    } = props;

    const failedCount = requestedClassCount * requestedContentCount;
    const studentListName = (studentList && studentList.name) || "";
    const studentListId = (studentList && studentList.id) || "";
    const classCode = (studentList && studentList.signupCode) || "";
    const toastProps: ToastProps = {
        testId: "assignment-toast-notification",
    };

    if (!isInAssignmentsTab()) {
        const assignmentFilter = createAction === "SAVE" ? "draft" : "active";
        if (requestedClassCount === 1) {
            toastProps.href =
                assignedToChildAssignmentsClassroom === "ALL"
                    ? `/parent/child-assignments/${classCode}/manage-assignments`
                    : `/coach/class/${studentListId}/manage-assignments?assignmentFilter=${assignmentFilter}`;
            toastProps.linkText =
                createAction === "SAVE"
                    ? t`View saved assignments`
                    : t`View assignments`;
        } else {
            toastProps.href =
                assignedToChildAssignmentsClassroom === "ALL"
                    ? "/parent/dashboard"
                    : "/teacher/dashboard";
            toastProps.linkText = t`Go to dashboard`;
        }
    }

    if (successContentCount === 0 || successClassCount === 0) {
        // Failed completely. Need to show error toast.
        // Note that if one of successContentCount and successClassCount is 0,
        // there shouldn't be any success at all.
        return (
            <BaseToastNotification isError={true}>
                {ToastCopyMap[createAction]["ERROR"](
                    requestedContentCount,
                    failedCount,
                    contentTitle,
                )}
            </BaseToastNotification>
        );
    }
    if (requestedContentCount === 1 && requestedClassCount === 1) {
        if (assignedToChildAssignmentsClassroom === "ALL") {
            return (
                <BaseToastNotification {...toastProps}>
                    {ToastCopyMap[createAction][
                        "SINGLE_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM"
                    ](contentTitle, studentListName)}
                </BaseToastNotification>
            );
        }
        return (
            <BaseToastNotification {...toastProps}>
                {ToastCopyMap[createAction]["SINGLE_CONTENT_SINGLE_CLASS"](
                    contentTitle,
                    studentCount,
                    studentListName,
                )}
            </BaseToastNotification>
        );
    }
    if (requestedContentCount === 1 && requestedClassCount > 1) {
        if (assignedToChildAssignmentsClassroom === "ALL") {
            return (
                <BaseToastNotification {...toastProps}>
                    {ToastCopyMap[createAction][
                        "SINGLE_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOMS"
                    ](contentTitle, successClassCount)}
                </BaseToastNotification>
            );
        }
        return (
            <BaseToastNotification {...toastProps}>
                {ToastCopyMap[createAction]["SINGLE_CONTENT_MULTI_CLASS"](
                    contentTitle,
                    successClassCount,
                )}
            </BaseToastNotification>
        );
    }
    if (requestedContentCount > 1 && requestedClassCount === 1) {
        if (assignedToChildAssignmentsClassroom === "ALL") {
            return (
                <BaseToastNotification {...toastProps}>
                    {ToastCopyMap[createAction][
                        "MULTI_CONTENT_SINGLE_CHILD_ASSIGNMENTS_CLASSROOM"
                    ](successContentCount, studentListName)}
                </BaseToastNotification>
            );
        }
        return (
            <BaseToastNotification {...toastProps}>
                {ToastCopyMap[createAction]["MULTI_CONTENT_SINGLE_CLASS"](
                    successContentCount,
                    studentCount,
                    studentListName,
                )}
            </BaseToastNotification>
        );
    }
    if (assignedToChildAssignmentsClassroom === "ALL") {
        return (
            <BaseToastNotification {...toastProps}>
                {ToastCopyMap[createAction][
                    "MULTI_CONTENT_MULTI_CHILD_ASSIGNMENTS_CLASSROOM"
                ](successContentCount, successClassCount)}
            </BaseToastNotification>
        );
    }
    return (
        <BaseToastNotification {...toastProps}>
            {ToastCopyMap[createAction]["MULTI_CONTENT_MULTI_CLASS"](
                successContentCount,
                successClassCount,
            )}
        </BaseToastNotification>
    );
};
